/** V1 Signal source gating shared by Campaign wizard + Ask Maya modal. */

export const ACTIVE_SIGNAL_SOURCES = new Set(["rnc", "custom"]);

export const COMING_SOON_MESSAGE = "This feature is coming in upcoming versions.";

/** Toast when a future Signal is clicked in Assign Signal / catalog UIs. */
export const UPCOMING_SIGNALS_MESSAGE =
  "These signals will come in upcoming releases.";

export const ACTIVE_SIGNAL_CONFLICT_MESSAGE =
  "This signal is already assigned to another active campaign.";

/** Toast when set-default hits an overlapping default campaign (matches backend). */
export const DEFAULT_SIGNAL_CONFLICT_MESSAGE =
  "Another campaign is already the default for one or more of these signals. "
  + "Clear that default first before setting this campaign as default.";

export const SIGNALS_LOCKED_MESSAGE =
  "Cannot change assigned signals while this campaign has running signals.";

/** True when the campaign KPI reports in-flight enrollments (active or paused). */
export function campaignHasRunningSignals(campaign) {
  return Number(campaign?.runningSignalsCount ?? campaign?.running_signals_count ?? 0) > 0;
}

/** Workflow transport channels (not Signal sources). */
export const TRANSPORT_CHANNEL_KEYS = new Set([
  "email", "linkedin", "whatsapp", "sms", "telegram", "voice",
]);

/** Default transport channels for AI sequence design (not Signal sources). */
export const DEFAULT_SEQUENCE_DESIGN_CHANNELS = ["email", "whatsapp", "telegram"];

export function isActiveSignalSource(key) {
  return ACTIVE_SIGNAL_SOURCES.has(key);
}

function _campaignSignalKeys(campaign) {
  if (!campaign) return [];
  if (Array.isArray(campaign.assignedSignals) && campaign.assignedSignals.length) {
    return campaign.assignedSignals.map(String);
  }
  if (Array.isArray(campaign.signalSources) && campaign.signalSources.length) {
    return campaign.signalSources.map(String);
  }
  const settings = campaign.settings && typeof campaign.settings === "object"
    ? campaign.settings
    : {};
  if (Array.isArray(settings.signal_sources) && settings.signal_sources.length) {
    return settings.signal_sources.map(String);
  }
  const channels = campaign.assignedChannels || campaign.channels || [];
  return channels.map(String);
}

function _isActiveAndNotExcluded(camp, excludeCampaignId) {
  return Boolean(
    camp &&
      !(excludeCampaignId && String(camp.id) === String(excludeCampaignId)) &&
      String(camp.status || "").toLowerCase() === "active"
  );
}

/**
 * Local fallback: detect active-campaign Signal conflicts from a campaigns list.
 */
export function findLocalActiveSignalConflicts(campaigns, signalSources, excludeCampaignId = null) {
  const claim = new Set(
    (signalSources || []).filter((k) => isActiveSignalSource(k)).map(String),
  );
  if (!claim.size) return [];
  const conflicts = [];
  for (const camp of campaigns || []) {
    if (!_isActiveAndNotExcluded(camp, excludeCampaignId)) continue;
    for (const src of _campaignSignalKeys(camp)) {
      if (claim.has(src)) {
        conflicts.push({
          signalSource: src,
          campaignId: camp.id,
          campaignName: camp.campaignName || camp.name || "Untitled campaign",
        });
      }
    }
  }
  return conflicts;
}

/**
 * Returns true when an active campaign already claims any of `signalSources`.
 * Prefers the dedicated API; falls back to listCampaigns so a missing route
 * cannot silently allow duplicate assignment.
 */
export async function hasActiveSignalConflict(api, signalSources, excludeCampaignId = null) {
  const allowed = (signalSources || []).filter((k) => isActiveSignalSource(k));
  if (!allowed.length) return false;

  try {
    if (typeof api.checkSignalConflicts === "function") {
      const res = await api.checkSignalConflicts(allowed, excludeCampaignId);
      if (Array.isArray(res?.conflicts) && res.conflicts.length > 0) return true;
      // Explicit empty conflicts from API — trust it.
      if (Array.isArray(res?.conflicts)) return false;
    }
  } catch {
    // Fall through to listCampaigns.
  }

  if (typeof api.listCampaigns !== "function") return false;
  const listed = await api.listCampaigns();
  const campaigns = listed?.campaigns || listed || [];
  return findLocalActiveSignalConflicts(campaigns, allowed, excludeCampaignId).length > 0;
}
